import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { testDatastoreConnectionDuringCreation } from "@/helpers/apis";
import { handleConnectionTestToast } from "../utils";
import {
  TestDatastoreConnectionDuringCreationRequest,
  TestDatastoreConnectionDuringCreationResponse,
} from "../../../../types";

interface ConnectionTestHookResult {
  connectionStatus: "untested" | "success" | "failed";
  isTestingConnection: boolean;
  testDatastoreConnection: (data: TestDatastoreConnectionDuringCreationRequest) => void;
  /** Whether the PAT can publish workbooks. ``null`` until a successful test
   * (or when the backend doesn't surface it). Drives the Step 3 'Create
   * Altimate Workbooks' UI branching. */
  canPublishWorkbooks: boolean | null;
}

export const useTableauConnectionTest = (): ConnectionTestHookResult => {
  const [connectionStatus, setConnectionStatus] = useState<"untested" | "success" | "failed">(
    "untested"
  );
  const [canPublishWorkbooks, setCanPublishWorkbooks] = useState<boolean | null>(null);

  const { mutate: testDatastoreConnection, isPending: isTestingConnection } = useMutation({
    mutationFn: (data: Partial<TestDatastoreConnectionDuringCreationRequest>) => {
      setConnectionStatus("untested");
      setCanPublishWorkbooks(null);
      return testDatastoreConnectionDuringCreation(data);
    },
    onSuccess: response => {
      setConnectionStatus(response?.ok ? "success" : "failed");
      if (response?.ok) {
        setCanPublishWorkbooks(response.can_publish_workbooks ?? null);
      }
      handleConnectionTestToast(response);
    },
    onError: (response: TestDatastoreConnectionDuringCreationResponse) => {
      setConnectionStatus("failed");
      handleConnectionTestToast(response);
    },
  });

  return {
    connectionStatus,
    isTestingConnection,
    testDatastoreConnection,
    canPublishWorkbooks,
  };
};
